import React, { useState, useRef } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, RadiologyRequest } from '../../types';
import { Scan, CheckCircle, Upload, X } from 'lucide-react';
export const RadiologyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    rad: RadiologyRequest;
  } | null>(null);
  const [report, setReport] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Find encounters with pending radiology requests
  const pendingRequests = encounters.flatMap(enc => enc.radiologyRequests.filter(rad => rad.status === 'Pending').map(rad => ({
    enc,
    rad
  })));
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    rad: RadiologyRequest;
  }) => {
    setSelectedRequest(item);
    setReport('');
    setImages([]);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      // Check size (50MB limit as per requirements)
      if (file.size > 50 * 1024 * 1024) {
        alert('File exceeds 50MB limit');
        return;
      }
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setImages(prev => [...prev, event.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };
  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    if (!report && images.length === 0) {
      alert('Please provide either a report or images before sending.');
      return;
    }
    const {
      enc,
      rad
    } = selectedRequest;
    const updatedRad: RadiologyRequest = {
      ...rad,
      status: 'Completed',
      report,
      images,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id,
      radiographerName: currentUser.fullName
    };
    const updatedRadRequests = enc.radiologyRequests.map(r => r.id === rad.id ? updatedRad : r);
    // Check if all rads are completed
    const allRadsCompleted = updatedRadRequests.every(r => r.status === 'Completed');
    const updateData: Partial<Encounter> = {
      radiologyRequests: updatedRadRequests,
      pendingRadiology: !allRadsCompleted
    };
    // If all rads done and no pending lab, move back to clinician
    if (allRadsCompleted && !enc.pendingLab) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Scan results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-1264">Patient not found</div>;
    const isSendDisabled = !report && images.length === 0;
    return <div className="space-y-6" data-id="element-1265">
        <div className="flex justify-between items-center" data-id="element-1266">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1267">
            Radiology Results Entry
          </h1>
          <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-1268">
            Back to Queue
          </Button>
        </div>

        <Card className="bg-purple-50 border-purple-200" data-id="element-1269">
          <div className="flex justify-between items-start" data-id="element-1270">
            <div data-id="element-1271">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1272">
                {patient.firstName} {patient.lastName}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-1273">
                BMC: {patient.bmcFileNumber} • {patient.age} yrs • {patient.sex}
              </p>
            </div>
            <div className="text-right" data-id="element-1274">
              <Badge variant="purple" className="text-sm" data-id="element-1275">
                {selectedRequest.rad.scanType}
              </Badge>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1276">
                Requested:{' '}
                {new Date(selectedRequest.rad.requestedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1277">
          <Card title="Upload Images" data-id="element-1278">
            <div className="space-y-4" data-id="element-1279">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 cursor-pointer" onClick={() => fileInputRef.current?.click()} data-id="element-1280">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" data-id="element-1281" />
                <p className="text-sm text-gray-600" data-id="element-1282">
                  Click to upload images (PNG/JPG)
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1283">
                  Max size: 50MB per image
                </p>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/png, image/jpeg" multiple onChange={handleImageUpload} data-id="element-1284" />
              </div>

              {images.length > 0 && <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-1285">
                  {images.map((img, idx) => <div key={idx} className="relative group" data-id="element-1286">
                      <img src={img} alt={`Scan ${idx}`} className="h-24 w-full object-cover rounded border border-gray-200" data-id="element-1287" />
                      <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1288">
                        <X className="w-4 h-4" data-id="element-1289" />
                      </button>
                    </div>)}
                </div>}
            </div>
          </Card>

          <Card title="Radiologist Report" data-id="element-1290">
            <div className="space-y-4 h-full flex flex-col" data-id="element-1291">
              <textarea className="w-full flex-1 min-h-[200px] border border-gray-300 rounded-md p-3 focus:ring-purple-500 focus:border-purple-500" placeholder="Enter detailed report findings here..." value={report} onChange={e => setReport(e.target.value)} data-id="element-1292" />
              <div className="text-sm text-gray-500 text-right" data-id="element-1293">
                Report by: {currentUser?.fullName}
              </div>
            </div>
          </Card>
        </div>

        <div className="flex justify-end" data-id="element-1294">
          <Button onClick={handleSaveResults} size="lg" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isSendDisabled} data-id="element-1295">
            <CheckCircle className="w-5 h-5 mr-2" data-id="element-1296" /> Send to Clinician
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1297">
      <div className="flex justify-between items-center" data-id="element-1298">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1299">
          Radiology Dashboard
        </h1>
        <Badge variant="purple" className="text-sm px-3 py-1" data-id="element-1300">
          {pendingRequests.length} Pending Scans
        </Badge>
      </div>

      <Card noPadding data-id="element-1301">
        <ul className="divide-y divide-gray-200" data-id="element-1302">
          {pendingRequests.length > 0 ? pendingRequests.map(item => {
          const patient = getPatient(item.enc.patientId);
          if (!patient) return null;
          return <li key={item.rad.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1303">
                  <div className="flex items-center" data-id="element-1304">
                    <div className="bg-purple-100 p-2 rounded-full mr-4" data-id="element-1305">
                      <Scan className="h-6 w-6 text-purple-600" data-id="element-1306" />
                    </div>
                    <div data-id="element-1307">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1308">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1309">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm font-medium text-purple-700" data-id="element-1310">
                        {item.rad.scanType}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectRequest(item)} className="bg-purple-600 hover:bg-purple-700 text-white" data-id="element-1311">
                    Upload Scan
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1312">
              <Scan className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1313" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1314">
                No pending scan requests
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};